import { ApolloQueryResult, useQuery } from '@apollo/client';

import { formatBirthdayForRewards } from 'shared/helpers/rewards';
import { GetRewardsV2Document } from 'src/gql/graphql';
import type { GetRewardsV2Query } from 'src/gql/graphql';
import type CartStore from 'src/state/cart';

type UseGetRewardsProps = {
  dispensaryId?: string;
  enterpriseId?: string;
  isoRegionCode?: string;
  birthday: string;
  pin: string;
  phone: string;
  skip?: boolean;
  customerId?: string;
  automaticAiqRequest?: {
    cart: CartStore;
    enabled: boolean;
    isAlpineIQEnabled: boolean;
    userId: string | null;
  };
  cartSubtotal?: number;
};

type UseGetRewardsReturn = {
  data: GetRewardsV2Query | undefined;
  /** True only while the *first* fetch for the current skip/variable set is in flight and no data (including stale `previousData`) is available yet. A `cartSubtotal` change triggers a `network-only` refetch, but we don't want that refetch to read as "loading" to callers — that would unmount UI that already has data to show. Use `networkLoading` instead if you specifically need Apollo's raw in-flight status (e.g. a spinner on a manual refetch button). */
  loading: boolean;
  /** Optional: Apollo's raw in-flight status, not adjusted for the previousData fallback. */
  networkLoading?: boolean;
  refetch: () => Promise<ApolloQueryResult<GetRewardsV2Query>>;
};

export const useGetRewards = ({
  dispensaryId,
  enterpriseId,
  isoRegionCode,
  birthday,
  pin = '',
  phone,
  skip = false,
  customerId,
  automaticAiqRequest,
  cartSubtotal,
}: UseGetRewardsProps): UseGetRewardsReturn => {
  const skipQuery = skip || (!dispensaryId && !enterpriseId);
  const dob = formatBirthdayForRewards(birthday);
  const variables = {
    phoneNumber: phone,
    dispensaryId,
    enterpriseId,
    isoRegionCode,
    dob,
    pin,
    customerId,
    cartSubtotal,
  };
  const automaticAiqContext =
    automaticAiqRequest?.enabled && automaticAiqRequest.isAlpineIQEnabled && dispensaryId
      ? automaticAiqRequest.cart.getAutomaticAiqOneShotContext({
          dispensaryId,
          userId: automaticAiqRequest.userId,
          customerId: customerId ?? null,
          phoneNumber: phone,
          dob,
          enterpriseId: enterpriseId ?? null,
          isoRegionCode: isoRegionCode ?? null,
          pinDecision: pin === 'skip' ? 'skip' : 'blank',
          integration: 'alpineiq',
          origin: 'automatic-aiq',
        })
      : undefined;

  const { data, previousData, loading, refetch } = useQuery(GetRewardsV2Document, {
    variables,
    fetchPolicy: 'network-only',
    skip: skipQuery,
    ...(automaticAiqContext && { context: automaticAiqContext }),
  });

  return {
    // Fall back to previousData while a cartSubtotal-triggered refetch is in flight, so callers
    // that gate rendering on `data` don't unmount/blank existing UI on every cart edit.
    data: data ?? previousData,
    loading: loading && data === undefined && previousData === undefined,
    networkLoading: loading,
    refetch,
  };
};
